import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import DataTable, { Column, DataTableProps } from '@repo/ui/data-table'
import { PharmacyIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import { useObserver } from '../../../lib/contexts/observer'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { ContactEnum } from '../../../lib/types/enums/enrollment/contact.enum'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import {
  TripFolderEnrollment,
  TripFolderPassengersFilters
} from '../../../lib/types/models/trip-folder/trip-folder-passengers'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import SupervisorAssignmentCell from './supervisor-assignment-cell'

interface TripFolderYoungPassengersListProps extends Pick<
  DataTableProps<TripFolderEnrollment, undefined>,
  'selectable' | 'selectedData' | 'onSelectedDataChange'
> {
  data: TripFolderEnrollment[]
  folderId: number
  selectedFilters: TripFolderPassengersFilters

  disableSplitting?: boolean
  isLoading?: boolean
}

const TripFolderEnrollmentsList = ({
  data,
  isLoading,
  folderId,
  disableSplitting,
  selectedFilters,
  ...tableProps
}: TripFolderYoungPassengersListProps) => {
  const tripFolderEnrollments = useObserver({
    key: 'trip-folder-young-passengers'
  })

  const columns: Column<TripFolderEnrollment>[] = [
    {
      accessorKey: 'enrollmentNumber',
      header: "N° d'inscription",
      cell: ({ row }) => valueRender(row.original.enrollmentNumber)
    },

    {
      accessorKey: 'childLastName',
      header: 'Nom',
      cell: ({ row }) => valueRender(row.original.childLastName?.toUpperCase())
    },
    {
      accessorKey: 'childFirstName',
      header: 'Prénom',
      cell: ({ row }) => (
        <Typography variant="secondary-body" className="text-grey">
          {row.original.childFirstName
            ? StringFormatter.capitalize(row.original.childFirstName)
            : ''}
        </Typography>
      )
    },
    {
      accessorKey: 'hasMedicalCondition',
      header: '',
      cell: ({ row }) => {
        if (row.original.hasMedicalCondition)
          return (
            <PharmacyIcon
              className="!size-5 shrink-0"
              color={tailwindColors.green.DEFAULT}
              color2={tailwindColors.green.light.DEFAULT}
            />
          )
      }
    },
    {
      accessorKey: 'childBirthDate',
      header: 'Âge',
      cell: ({ row }) => EnrollmentProperties.getAgeAtDeparture(row.original)?.toString() ?? '-'
    },
    {
      accessorKey: 'childGender',
      header: 'Sexe',
      cell: ({ row }) =>
        valueRender(row.original.childGender, (gender) => genderOneLetterMapping[gender].label)
    },
    {
      accessorKey: 'childNationality',
      header: 'Nationalité',
      cell: ({ row }) => valueRender(row.original.childNationality)
    },
    {
      accessorKey: 'reservationNumber',
      header: 'N° Résa',
      cell: ({ row }) => valueRender(row.original.reservationNumber)
    },
    {
      accessorKey: 'enrollmentCenter',
      header: 'Centre',
      cell: ({ row }) => {
        return valueRender(row.original.enrollmentCenter?.center?.name)
      }
    },
    {
      accessorKey: 'declaredFriends',
      header: 'Ami(s)',
      cell: ({ row }) => (
        <div className="flex flex-col gap-2">
          {row.original.declaredFriends && row.original.declaredFriends.length > 0 ? (
            row.original.declaredFriends.map((friend) => (
              <div key={friend.id}>
                <Typography variant="label-semibold">
                  {toFullName(friend.firstName, friend.lastName)}
                </Typography>
              </div>
            ))
          ) : (
            <Typography variant="label-semibold">-</Typography>
          )}
        </div>
      )
    },
    {
      accessorKey: 'mainProposition',
      header: 'Ville de départ',
      cell: ({ row }) => {
        const mainProposition = row.original.mainProposition
        // Display the Departure of the secondary trip if the enrollment has a secondary trip and the departure of the main trip if not.
        const departureLabel =
          TripProperties.getDepartureOrJourneyPlaceLabel(mainProposition?.secondaryTrip) ||
          TripProperties.getDepartureOrJourneyPlaceLabel(mainProposition?.mainTrip)

        return valueRender(departureLabel)
      }
    },
    {
      accessorKey: 'parent1PhoneNumber',
      header: 'N° tel parent 1',
      cell: ({ row }) => (
        <div className="flex flex-col gap-2 py-2">
          <Typography variant="secondary-body">
            {valueRender(row.original.parent1PhoneNumber, (phoneNumber) =>
              formatPhoneNumber(phoneNumber)
            )}
          </Typography>
          {row.original.contactPriority === ContactEnum.PARENT_1 && (
            <Badge variant="warning" className="h-fit py-1" text="Contact prioritaire" />
          )}
        </div>
      )
    },
    {
      accessorKey: 'parent2PhoneNumber',
      header: 'N° tel parent 2',
      cell: ({ row }) => (
        <div className="flex flex-col gap-2 py-2">
          <Typography variant="secondary-body">
            {valueRender(row.original.parent2PhoneNumber, (phoneNumber) =>
              formatPhoneNumber(phoneNumber)
            )}
          </Typography>
          {row.original.contactPriority === ContactEnum.PARENT_2 && (
            <Badge variant="warning" className="h-fit py-1" text="Contact prioritaire" />
          )}
        </div>
      )
    },
    {
      accessorKey: 'childPhoneNumber',
      header: 'N° tel jeune',
      cell: ({ row }) =>
        valueRender(row.original.childPhoneNumber, (phoneNumber) => formatPhoneNumber(phoneNumber))
    },
    {
      accessorKey: 'callSplit',
      header: 'Split appel',
      hidden: disableSplitting,
      cell: ({ row }) => {
        const { id: enrollmentId, supervisor } = row.original

        return (
          <SupervisorAssignmentCell
            enrollmentId={enrollmentId}
            supervisor={supervisor}
            selectedFilters={selectedFilters}
            tripFolderId={folderId}
            onSuccess={() => {
              tripFolderEnrollments.notify(['trip-folder-young-passengers'])
            }}
          />
        )
      }
    }
  ]

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Typography variant="text">Liste des jeunes</Typography>
        <Badge variant="fushia" text={`Total jeunes : ${data.length}`} />
      </div>

      <DataTable<TripFolderEnrollment>
        {...tableProps}
        disableHover
        isLoading={isLoading}
        columns={columns}
        data={data}
        rowCount={data.length}
        paginated={false}
      />
    </div>
  )
}

export default TripFolderEnrollmentsList
